import { Temporal, toTemporalInstant } from 'temporal-polyfill';

export * from './days-of-week.js';
export * from './formatting.js';
export * from './instant-interval.js';
export * from './instant-utils.js';
export * from './plain-date-interval.js';
export * from './plain-datetime-interval.js';
export * from './plain-time-interval-set.js';
export * from './plain-time-interval.js';
export * from './wrapped-plain-time-interval.js';

import { AirServerError } from '../custom-error.js';
import { BoundComparatorOps } from '../ordering/index.js';
import { InstantInterval } from './instant-interval.js';

/**
 * Converts a built-in `Date` into a `Temporal.Instant`.
 * @param date a built-in `Date`
 * @returns a `Temporal.Instant`
 */
export function instantFromDate(date: Date): Temporal.Instant {
  return toTemporalInstant.call(date);
}

/**
 * Convert a legacy `Date` object to a `Temporal.PlainDate` using the given time
 * zone, discarding all time information.
 *
 * Use with care, and double-check that you have the correct time zone.
 */
export function plainDateFromDate(date: Date, timeZone: Temporal.TimeZoneLike): Temporal.PlainDate {
  return Temporal.Instant.fromEpochMilliseconds(+date).toZonedDateTimeISO(timeZone).toPlainDate();
}

/**
 * Returns an ISO plain-time string (e.g. `"09:30:00"`) for the wall-clock time of
 * `instant` in {@link FrequentTimeZones.UsCentral}.
 */
export function plainTimeStringFromInstant(instant: Temporal.Instant): string {
  return instant.toZonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainTime().toString();
}

/**
 * Returns an ISO plain-date string (e.g. `"2026-04-25"`) for the calendar date of
 * `instant` in {@link FrequentTimeZones.UsCentral} (same zone as {@link plainTimeStringFromInstant}).
 */
export function plainDateStringFromInstant(instant: Temporal.Instant): string {
  return instant.toZonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate().toString();
}

/**
 * Converts an ISO 8601 instant string into a `Temporal.Instant`, or throws an
 * `AirServerError` if the string is not a valid instant.
 * @param input an ISO 8601 instant string
 * @returns a `Temporal.Instant`
 * @throws {AirServerError} if the input string is not a valid instant
 */
export function safeInstantFromString(input: string): Temporal.Instant {
  try {
    return Temporal.Instant.from(input);
  } catch (e) {
    throw new AirServerError('bad_request', `Invalid instant string: ${input}.`, { cause: e });
  }
}

export function plainDateFromStringOrElseUndefined(input: string | undefined | null): Temporal.PlainDate | undefined {
  if (!input) return undefined;
  try {
    return Temporal.PlainDate.from(input);
  } catch {
    return undefined;
  }
}

/**
 * Converts an ISO 8601 instant string into a `Temporal.Instant`, falling back
 * to a default value if the input is undefined or not a valid instant.
 *
 * @param input - An ISO 8601 instant string or undefined.
 * @param defaultValue - The default `Temporal.Instant` to return if the input
 * is undefined or invalid.
 * @returns A `Temporal.Instant` parsed from the input string, or the
 * default value if the input is undefined or invalid.
 */
export function instantFromStringWithDefault(
  input: string | undefined,
  defaultValue: Temporal.Instant,
): Temporal.Instant {
  if (!input) return defaultValue;
  try {
    return Temporal.Instant.from(input);
  } catch {
    return defaultValue;
  }
}

export function instantToDate(instant: Temporal.Instant): Date {
  return new Date(instant.epochMilliseconds);
}

export function instantToDateWithZone(instant: Temporal.Instant, timeZone: string): Date {
  const zoned = instant.toZonedDateTimeISO(timeZone);
  return new Date(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second, zoned.millisecond);
}

/**
 * A date range object returned by `react-big-calendar`'s month view.
 */
export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * A week range object returned by `react-big-calendar`'s week view. Its own
 * type definitions say that it is an array, but it is actually a tuple of seven
 * `Date` objects.
 */
export type WeekRange = Date[];

/**
 * Converts a `react-big-calendar` datetime range into an `InstantInverval`.
 * @param range a range provided by `react-big-calendar`
 * @param timeZone the time zone to use
 */
export function instantIntervalFromRange(
  range: WeekRange | DateRange,
  timeZone: Temporal.TimeZoneLike,
): InstantInterval {
  if ('end' in range) {
    return InstantInterval.from(instantFromDate(range.start), instantFromDate(range.end));
  }

  const start = instantFromDate(range[0]);
  const end = endOfDay(instantFromDate(range[6]), timeZone);
  return InstantInterval.from(start, end);
}

/**
 * Converts a `react-big-calendar` date range into an `InstantInterval` with the specified time zone.
 * @param range a range provided by `react-big-calendar`
 * @param timeZone the time zone to use
 * @returns an `InstantInterval` representing the date range
 */
export function instantIntervalFromDateRangeWithZone(
  range: DateRange,
  timeZone: Temporal.TimeZoneLike,
): InstantInterval {
  const toInstant = (date: Date) =>
    Temporal.ZonedDateTime.from({
      timeZone,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    }).toInstant();

  return InstantInterval.from(toInstant(range.start), toInstant(range.end));
}

/**
 * Returns the last millisecond of the day that `instant` belongs to, in the
 * given time zone.
 */
export function endOfDay(instant: Temporal.Instant, timeZone: Temporal.TimeZoneLike): Temporal.Instant {
  const zonedDateTime = instant.toZonedDateTimeISO(timeZone);
  return zonedDateTime.add({ days: 1 }).startOfDay().subtract({ milliseconds: 1 }).toInstant();
}

export function startOfDay(instant: Temporal.Instant, timeZone: Temporal.TimeZoneLike): Temporal.Instant {
  const zonedDateTime = instant.toZonedDateTimeISO(timeZone);
  return zonedDateTime.startOfDay().toInstant();
}

export function startOfDayFromPlainDate(
  plainDate: Temporal.PlainDate,
  timeZone: Temporal.TimeZoneLike,
): Temporal.Instant {
  const plainTime = Temporal.PlainTime.from('00:00');
  return plainDate.toZonedDateTime({ timeZone, plainTime }).toInstant();
}

/**
 * Converts a plain time from one time zone to another using the current date.
 *
 * This function takes a plain time in a source time zone and converts it to the equivalent
 * plain time in a target time zone. It uses plainDateUsedForConversion (defaults to Now) for the conversion,
 * which means the result can vary depending on when this function is called and/or what date is passed for plainDateUsedForConversion,
 * especially when dealing with time zones that have different daylight saving time rules.
 *
 * @param plainTime - The plain time to convert
 * @param fromTimeZone - The source time zone
 * @param toTimeZone - The target time zone
 * @param plainDateUsedForConversion - Optional date to use for the conversion (defaults to today's date in UTC)
 * @returns The equivalent plain time in the target time zone
 *
 * @example
 * // Converting 2:00 PM from US/Eastern to US/Pacific (both observe DST)
 * // During standard time (winter): 2:00 PM ET → 11:00 AM PT (3 hour difference)
 * // During daylight time (summer): 2:00 PM ET → 11:00 AM PT (still 3 hour difference)
 * const easternTime = Temporal.PlainTime.from('14:00');
 * const pacificTime = adjustPlainTimeAcrossTZ(easternTime, 'US/Eastern', 'US/Pacific');
 *
 * @example
 * // Converting 1:00 PM from US/Arizona (no DST) to US/Central (observes DST)
 * // During standard time (winter): 1:00 PM MST → 2:00 PM CST (1 hour ahead)
 * // During daylight time (summer): 1:00 PM MST → 3:00 PM CDT (2 hours ahead)
 * const arizonaTime = Temporal.PlainTime.from('13:00');
 * const centralTime = adjustPlainTimeAcrossTZ(arizonaTime, 'US/Arizona', 'US/Central');
 *
 * @remarks
 * IMPORTANT: Since this function uses the current date for the conversion by default, the result
 * will depend on whether daylight saving time is in effect in either time zone on the
 * current date. This means the same function call can produce different results at
 * different times of the year.
 */
export function adjustPlainTimeAcrossTZ(
  plainTime: Temporal.PlainTime,
  fromTimeZone: string,
  toTimeZone: string,
  plainDateUsedForConversion: Temporal.PlainDate = Temporal.Now.plainDateISO('UTC'),
): Temporal.PlainTime {
  const zonedDateTime = plainDateUsedForConversion.toZonedDateTime({ timeZone: fromTimeZone, plainTime });
  return zonedDateTime.withTimeZone(toTimeZone).toPlainTime();
}

/**
 * A utility for comparing two Temporal.Instants that is more descriptive than
 * the built-in `Temporal.Instant.compare`.
 */
class InstantCompareOps extends BoundComparatorOps<Temporal.Instant> {
  isBefore = this.lt;
  isAfter = this.gt;
  isBeforeOrEqual = this.lte;
  isAfterOrEqual = this.gte;
}

export const InstantCompare = (a: Temporal.Instant) =>
  new InstantCompareOps(Temporal.Instant.compare, a);

export const FrequentTimeZones = {
  UsCentral: 'US/Central',
  UsPacific: 'US/Pacific',
  UTC: 'UTC',
} as const;

/** Canonical IANA zone for US Central — Postgres may not recognize legacy `US/Central`. */
export const POSTGRES_US_CENTRAL = 'America/Chicago';

export function formatCardTimeRange(startTime: string, endTime: string): string {
  const startLower = startTime.toLowerCase();
  const endLower = endTime.toLowerCase();

  // Check if both times have the same period (am or pm)
  const startHasAm = startLower.includes(' am');
  const startHasPm = startLower.includes(' pm');
  const endHasAm = endLower.includes(' am');
  const endHasPm = endLower.includes(' pm');

  // If both have am or both have pm, remove the period from the start time
  if ((startHasAm && endHasAm) || (startHasPm && endHasPm)) {
    const startWithoutPeriod = startLower.replace(/ (am|pm)/, '');
    return `${startWithoutPeriod} - ${endLower}`;
  }

  // If periods are different, keep both
  return `${startLower} - ${endLower}`;
}

export function maxPlainDate(a: Temporal.PlainDate, b: Temporal.PlainDate): Temporal.PlainDate {
  return Temporal.PlainDate.compare(a, b) >= 0 ? a : b;
}

export function isSameDay(start: Temporal.Instant, end: Temporal.Instant, timeZone: string): boolean {
  const startZoned = start.toZonedDateTimeISO(timeZone);
  const endZoned = end.toZonedDateTimeISO(timeZone);
  return startZoned.toPlainDate().equals(endZoned.toPlainDate());
}
